import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import * as authAPI from '../services/authService';
import './LoginPage.css';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();

  const [step, setStep] = useState('telegram'); // 'telegram' or 'otp'
  const [telegramId, setTelegramId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!telegramId.trim()) {
      setError(t('telegram_id_required'));
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.requestOTP(telegramId);
      setMessage(response.message || t('otp_sent_success'));
      setStep('otp');
    } catch (err) {
      setError(err.message || t('otp_request_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!otpCode.trim()) {
      setError(t('otp_code_required'));
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.verifyOTP(telegramId, otpCode);

      // Save tokens and user data
      login(
        {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
        },
        response.user
      );

      setMessage(t('login_success'));
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
    } catch (err) {
      setError(err.message || t('otp_verification_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('telegram');
    setOtpCode('');
    setError('');
    setMessage('');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Language Toggle */}
        <button className="language-toggle" onClick={toggleLanguage}>
          {language === 'uz' ? 'EN' : 'UZ'}
        </button>

        {/* Logo and Title */}
        <div className="login-header">
          <div className="logo">
            <span className="logo-icon">🍽️</span>
          </div>
          <h1>{t('app_name')}</h1>
          <p className="subtitle">{t('app_description')}</p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            {error}
          </div>
        )}

        {message && (
          <div className="alert alert-success">
            <span className="alert-icon">✓</span>
            {message}
          </div>
        )}

        {/* Login Form */}
        {step === 'telegram' ? (
          <form onSubmit={handleRequestOTP} className="login-form">
            {/* Telegram Bot Instructions */}
            <div className="telegram-instructions">
              <div className="instruction-step">
                <span className="step-number">1</span>
                <p>Telegram botni oching: <a href="https://t.me/AnyFoodAI_bot" target="_blank" rel="noopener noreferrer" className="bot-link">@AnyFoodAI_bot</a></p>
              </div>
              <div className="instruction-step">
                <span className="step-number">2</span>
                <p><strong>/start</strong> buyrug'ini yuboring</p>
              </div>
              <div className="instruction-step">
                <span className="step-number">3</span>
                <p>Bot sizga Telegram ID ni yuboradi</p>
              </div>
              <div className="instruction-step">
                <span className="step-number">4</span>
                <p>ID ni quyidagi maydonga kiriting:</p>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="telegram-id">{t('telegram_id_label')}</label>
              <input
                id="telegram-id"
                type="text"
                value={telegramId}
                onChange={(e) => setTelegramId(e.target.value)}
                placeholder={t('telegram_id_placeholder')}
                disabled={loading}
                className="form-input"
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('sending') : t('send_otp')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="login-form">
            <div className="form-group">
              <label htmlFor="otp-code">{t('otp_code_label')}</label>
              <input
                id="otp-code"
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                placeholder={t('otp_code_placeholder')}
                disabled={loading}
                className="form-input otp-input"
                maxLength="6"
              />
              <small className="form-hint">{t('otp_code_hint')}</small>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={handleBack}
                className="btn btn-secondary"
                disabled={loading}
              >
                {t('back')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? t('verifying') : t('verify_login')}
              </button>
            </div>
          </form>
        )}

        {/* Footer */}
        <div className="login-footer">
          <p className="help-text">{t('need_help')}</p>
          <p className="telegram-info">
            {t('telegram_bot_info')}: <strong>@AnyFoodAI_bot</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
